import { useEffect } from "react";

import { useAppSelector } from "@/redux/store/hooks";
import usePinned from "@/hooks/usePinned";
import { Box } from "@/storybook-components";

const TRANSLATE_SCRIPT_SRC =
  "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";

const PAGE_LANGUAGE_PATTERN = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;

/*
 * Google appends its "Translated by Google" banner as an iframe directly under
 * body and marks it with the stable skiptranslate class. Matching on structure
 * avoids depending on Google's obfuscated build-generated class names, which
 * change between releases. The iframe qualifier matters: the visible language
 * gadget also carries skiptranslate, so a bare class match hides our dropdown.
 * Google also offsets body to make room for the banner.
 *
 * Set via dangerouslySetInnerHTML because React escapes style children during
 * SSR, which turns the child combinator into &gt; and breaks the rule.
 */
const BANNER_CSS = `
  body > iframe.skiptranslate {
    display: none !important;
  }
  body {
    top: 0 !important;
    position: static !important;
  }
`;

const safePageLanguage = (locale: unknown) =>
  typeof locale === "string" && PAGE_LANGUAGE_PATTERN.test(locale)
    ? locale
    : "en";

type GoogleTranslateWindow = typeof window & {
  googleTranslateElementInit?: () => void;
  google?: {
    translate?: {
      TranslateElement: new (
        options: {
          pageLanguage: string;
          includedLanguages?: string;
          autoDisplay?: boolean;
        },
        elementId: string
      ) => void;
    };
  };
};

/**
 * Hidden Google Website Translator instance for pinned survey iframes.
 *
 * The parent project page owns the visible language dropdown. Pinned surveys
 * load in a same-origin iframe without that widget, so page 2+ never
 * re-translates. This mounts TranslateElement off-screen so the shared
 * googtrans cookie can be re-applied after each survey page change.
 *
 * The widget is created from the JS API (not interpolated HTML) so locale
 * cannot be injected into a script string.
 */
const HiddenGoogleTranslate = () => {
  const { googleTranslationEnabled } = useAppSelector(
    (state) => state.settings.settings
  );
  const { locale } = useAppSelector((state) => state.site.site);
  const { nonce } = useAppSelector((state) => state.adapter.adapter);
  const { isPinned } = usePinned();

  const shouldMount = Boolean(googleTranslationEnabled && isPinned);
  const pageLanguage = safePageLanguage(locale);

  useEffect(() => {
    if (!shouldMount) return undefined;

    const googleWindow = window as GoogleTranslateWindow;
    googleWindow.googleTranslateElementInit = () => {
      const TranslateElement = googleWindow.google?.translate?.TranslateElement;
      if (!TranslateElement) return;
      new TranslateElement(
        {
          pageLanguage,
          includedLanguages: "",
          autoDisplay: false
        },
        "google-translate-element"
      );
    };

    let script = document.querySelector(
      `script[src="${TRANSLATE_SCRIPT_SRC}"]`
    );
    let createdScript = false;
    if (!script) {
      const newScript = document.createElement("script");
      newScript.src = TRANSLATE_SCRIPT_SRC;
      newScript.async = true;
      if (nonce) newScript.setAttribute("nonce", nonce);
      document.body.appendChild(newScript);
      script = newScript;
      createdScript = true;
    }

    return () => {
      if (createdScript) script?.remove();
      delete googleWindow.googleTranslateElementInit;
    };
  }, [shouldMount, pageLanguage, nonce]);

  if (!shouldMount) return null;

  return (
    <Box
      id="google-translate-element"
      aria-hidden="true"
      pos="absolute"
      w="1px"
      h="1px"
      overflow="hidden"
      css={{ clip: "rect(0 0 0 0)" }}
    >
      <style nonce={nonce} dangerouslySetInnerHTML={{ __html: BANNER_CSS }} />
    </Box>
  );
};

export default HiddenGoogleTranslate;
